import React, { useState, useEffect, useCallback } from 'react';
import FileDropzone from './FileDropzone';
import FileScanResults from './FileScanResults';
import FileUploadProgress from './FileUploadProgress';
import clientHelperService from '../services/clientHelperService';

const FileUploadWizard = ({ isOpen, onClose, selectedClient, selectedClientName }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [scanResults, setScanResults] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isScanning, setIsScanning] = useState(false);
  const [allUploadsComplete, setAllUploadsComplete] = useState(false);
  const [allUploadsSuccessful, setAllUploadsSuccessful] = useState(false);

  const handleFilesSelected = (files) => {
    setSelectedFiles(files);
    // Reset scan results when files change
    setScanResults(null);
  };

  const handleRemoveFile = (fileToRemove) => {
    const updatedFiles = selectedFiles.filter(file => file !== fileToRemove);
    setSelectedFiles(updatedFiles);

    // Update scan results to remove the file
    if (scanResults) {
      const updatedScanResults = scanResults.filter(result => result.file !== fileToRemove);
      setScanResults(updatedScanResults);
    }

    // If no files left, go back to step 1
    if (updatedFiles.length === 0) {
      setCurrentStep(1);
      setScanResults(null);
    }
  };

  const handleNextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      // Reset scan results when going back from step 3 to 2
      if (currentStep === 3) {
        setScanResults(null);
      }
    }
  };

  const handleScanFiles = useCallback(async () => {
    if (isScanning) return; // Prevent multiple simultaneous scans

    setIsScanning(true);
    try {
      // Call the real API to check file existence
      const response = await clientHelperService.checkFileExistence({
        client_id: selectedClient,
        files: selectedFiles
      });

      // Transform the API response to match our expected format
      const transformedResults = response.results.map(result => ({
        file: selectedFiles.find(f => f.name === result.filename),
        exists: result.metadata.exists,
        lastModified: result.metadata.last_modified,
        size: result.metadata.size,
        contentType: result.metadata.content_type
      }));

      setScanResults(transformedResults);
    } catch (error) {
      console.error('Error scanning files:', error);
      // Fallback to mock data for demo purposes
      const mockResults = selectedFiles.map(file => ({
        file,
        exists: Math.random() > 0.7 // 30% chance file already exists
      }));
      setScanResults(mockResults);
    } finally {
      setIsScanning(false);
    }
  }, [isScanning, selectedClient, selectedFiles]);

  // Auto-trigger scan when reaching step 2
  useEffect(() => {
    if (currentStep === 2 && selectedFiles.length > 0 && !scanResults && !isScanning) {
      handleScanFiles();
    }
    // If we're on step 2 and have scan results, but the file count doesn't match, rescan
    if (currentStep === 2 && scanResults && scanResults.length !== selectedFiles.length && !isScanning) {
      setScanResults(null); // This will trigger a rescan on the next effect
    }
  }, [currentStep, selectedFiles.length, scanResults, isScanning, handleScanFiles]);

  const handleUpload = async () => {
    try {
      console.log('Starting upload process for client:', selectedClient);
      console.log('Files to upload:', selectedFiles);

      let allUploadsSuccessful = true;

      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];

        // Initialize progress
        setUploadProgress(prev => ({
          ...prev,
          [file.name]: { progress: 0, status: 'uploading' }
        }));

        try {
          // Upload the file
          const result = await clientHelperService.uploadFile(
            file,
            selectedClient,
            (progress) => {
              setUploadProgress(prev => ({
                ...prev,
                [file.name]: { progress, status: 'uploading' }
              }));
            }
          );

          console.log(`Upload completed for ${file.name}:`, result);

          // Mark as completed
          setUploadProgress(prev => ({
            ...prev,
            [file.name]: { progress: 100, status: 'completed' }
          }));

        } catch (error) {
          console.error(`Upload failed for ${file.name}:`, error);
          allUploadsSuccessful = false;
          setUploadProgress(prev => ({
            ...prev,
            [file.name]: {
              progress: 0,
              status: 'error',
              error: error.message || 'Upload failed'
            }
          }));
        }
      }

      console.log('All uploads completed');

      // Set upload completion state
      setAllUploadsComplete(true);
      setAllUploadsSuccessful(allUploadsSuccessful);

    } catch (error) {
      console.error('Upload preparation failed:', error);

      // Mark all files as failed
      selectedFiles.forEach(file => {
        setUploadProgress(prev => ({
          ...prev,
          [file.name]: {
            progress: 0,
            status: 'error',
            error: 'Upload preparation failed'
          }
        }));
      });

      setAllUploadsComplete(true);
      setAllUploadsSuccessful(false);
    }
  };

  const handleClose = async () => {
    // Reset all state and close
    setCurrentStep(1);
    setSelectedFiles([]);
    setScanResults(null);
    setUploadProgress({});
    setAllUploadsComplete(false);
    setAllUploadsSuccessful(false);
    onClose();
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <FileDropzone
            onFilesSelected={handleFilesSelected}
            selectedFiles={selectedFiles}
          />
        );
      case 2:
        return (
          <div>
            <h3>Scanning Files</h3>
            {isScanning ? (
              <div className="text-center">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Scanning...</span>
                </div>
                <p className="mt-2">Checking for duplicate files...</p>
              </div>
            ) : (
              <FileScanResults
                files={selectedFiles}
                scanResults={scanResults}
                onScan={handleScanFiles}
                onRemoveFile={handleRemoveFile}
                isScanning={false}
              />
            )}
          </div>
        );
      case 3:
        return (
          <FileUploadProgress
            files={selectedFiles}
            progress={uploadProgress}
            onUpload={handleUpload}
          />
        );
      default:
        return null;
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return 'Select Files';
      case 2:
        return 'Scan Files';
      case 3:
        return 'Upload Files';
      default:
        return '';
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return selectedFiles.length > 0;
      case 2:
        return scanResults !== null;
      case 3:
        return true;
      default:
        return false;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-cloud-upload me-2"></i>
              File Upload Wizard - {getStepTitle()} for <span className="text-primary fw-bold">{selectedClientName}</span>
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
            ></button>
          </div>

          <div className="modal-body">
            {/* Progress indicator */}
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="badge bg-primary">Step {currentStep} of 3</span>
              </div>
              <div className="progress" style={{ height: '6px' }}>
                <div
                  className="progress-bar"
                  role="progressbar"
                  style={{ width: `${(currentStep / 3) * 100}%` }}
                  aria-valuenow={currentStep}
                  aria-valuemin="0"
                  aria-valuemax="3"
                ></div>
              </div>
            </div>

            {/* Step content */}
            {renderStepContent()}
          </div>

          <div className="modal-footer">
            {allUploadsComplete ? (
              // Show only Close button when uploads are complete
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleClose}
              >
                <>
                  <i className="bi bi-check-circle me-2"></i>
                  Close
                </>
              </button>
            ) : (
              // Show regular navigation buttons during the wizard
              <>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleClose}
                >
                  Cancel
                </button>

                {currentStep > 1 && (
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={handlePreviousStep}
                  >
                    <i className="bi bi-arrow-left me-2"></i>
                    Previous
                  </button>
                )}

                {currentStep < 3 ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleNextStep}
                    disabled={!canProceed()}
                  >
                    {currentStep === 2 && !scanResults ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Scanning...
                      </>
                    ) : (
                      <>
                        Next
                        <i className="bi bi-arrow-right ms-2"></i>
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={handleUpload}
                    disabled={Object.values(uploadProgress).some(p => p.status === 'uploading')}
                  >
                    <i className="bi bi-upload me-2"></i>
                    Upload Files
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileUploadWizard;
